import { useAppDispatch, useAppState } from "@/store/useApp";
import { RefObject } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { Block } from "./Block";
import { GroupSelector } from "./GroupSelector";
import { GroupSuggestions } from "./GroupSuggestions";
import { resolveSuggestion } from "./blockUtils";
import { cn } from "../../../lib/utils";

interface DocumentProps {
  selectedBlockRef: RefObject<HTMLDivElement>;
}

export const Document = ({
  selectedBlockRef,
}: DocumentProps) => {
  
  const document = useAppState((state) => state.currentDocument)!;
  const selectedBlockIndex = useAppState((state) => state.selectedBlockIndex);
  const expandedBlockId = useAppState((state) => state.expandedBlockId);
  const selectedGroup = useAppState((state) => state.selectedGroup);
  const focusMode = useAppState((state) => state.focusMode);
  const isReferencesPanelOpen = useAppState(
    (state) => state.referencePanel.visible
  );
  const classificationFilters = useAppState(
    (state) => state.classificationFilters
  );
  const dispatch = useAppDispatch();

  const blocks = !selectedGroup
    ? document.blocks
    : document.blocks.filter((block) =>
        selectedGroup.blockIds.includes(block.id)
      );

  const filteredBlocksWithSuggestions = blocks.map((block) => ({
    block,
    suggestion: resolveSuggestion(document, block.id, classificationFilters),
  }));

  // Add hotkeys for header navigation
  useHotkeys(
    "shift+up",
    () => {
      const headerTypes = ["header1", "header2", "header3", "header4"];
      const currentIndex = selectedBlockIndex;

      // Find the previous header
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (headerTypes.includes(filteredBlocksWithSuggestions[i].block.type)) {
          dispatch({
            type: "SET_SELECTED_BLOCK_INDEX",
            payload: i,
          });
          break;
        }
      }
    },
    {
      keydown: true,
      preventDefault: true,
    },
    [filteredBlocksWithSuggestions, selectedBlockIndex]
  );

  useHotkeys(
    "shift+down",
    () => {
      const headerTypes = ["header1", "header2", "header3", "header4"];
      const currentIndex = selectedBlockIndex;

      // Find the next header
      for (
        let i = currentIndex + 1;
        i < filteredBlocksWithSuggestions.length;
        i++
      ) {
        if (headerTypes.includes(filteredBlocksWithSuggestions[i].block.type)) {
          dispatch({
            type: "SET_SELECTED_BLOCK_INDEX",
            payload: i,
          });
          break;
        }
      }
    },
    {
      keydown: true,
      preventDefault: true,
    },
    [filteredBlocksWithSuggestions, selectedBlockIndex]
  );

  // Add hotkeys for highlight navigation
  useHotkeys(
    "alt+up",
    () => {
      const currentIndex = selectedBlockIndex;

      // Find the previous highlight
      for (let i = currentIndex - 1; i >= 0; i--) {
        const { block, suggestion } = filteredBlocksWithSuggestions[i];
        if (block.sentiment || suggestion) {
          dispatch({
            type: "SET_SELECTED_BLOCK_INDEX",
            payload: i,
          });
          break;
        }
      }
    },
    {
      keydown: true,
      preventDefault: true,
    },
    [filteredBlocksWithSuggestions, selectedBlockIndex]
  );

  useHotkeys(
    "alt+down",
    () => {
      const currentIndex = selectedBlockIndex;

      // Find the next highlight
      for (
        let i = currentIndex + 1;
        i < filteredBlocksWithSuggestions.length;
        i++
      ) {
        const { block, suggestion } = filteredBlocksWithSuggestions[i];
        if (block.sentiment || suggestion) {
          dispatch({
            type: "SET_SELECTED_BLOCK_INDEX",
            payload: i,
          });
          break;
        }
      }
    },
    {
      keydown: true,
      preventDefault: true,
    },
    [filteredBlocksWithSuggestions, selectedBlockIndex]
  );

  return (
    <main className="mx-auto max-w-2xl flex-1 flex flex-row items-start h-full pt-8">
      <div
        className={`pl-6 w-full flex flex-col h-full ${
          isReferencesPanelOpen ? "-ml-[200px]" : ""
        }`}
      >
        {!focusMode && selectedGroup?.title && <div></div>}
        <div className="overflow-y-auto flex-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] relative pl-6 pr-1 ml-[-30px] focus:outline-none">
          <h1 className={cn("ml-2.5", focusMode && "text-gray-300")}>{selectedGroup?.title}</h1>

          {(() => {
            const nodes: import("react").ReactNode[] = [];

            const isUnorderedListItemText = (b: { type: string; value: string }) => b.type === "text" && /^(\s*[-*+]\s+)/.test(b.value || "");
            const isOrderedListItemText = (b: { type: string; value: string }) => b.type === "text" && /^(\s*\d+[.)]\s+)/.test(b.value || "");
            const extractListNumber = (value: string): number | undefined => {
              const match = value.match(/^\s*(\d+)[.)]\s+/);
              return match ? parseInt(match[1], 10) : undefined;
            };

            for (let i = 0; i < filteredBlocksWithSuggestions.length; i++) {
              const { block, suggestion } = filteredBlocksWithSuggestions[i];
              const inUl = isUnorderedListItemText(block);
              const inOl = !inUl && isOrderedListItemText(block);

              if (!inUl && !inOl) {
                nodes.push(
                  <Block
                    key={block.id}
                    block={block}
                    index={i}
                    isSelected={i === selectedBlockIndex}
                    isExpanded={block.id === expandedBlockId}
                    focusMode={focusMode}
                    suggestion={suggestion}
                    ref={i === selectedBlockIndex ? selectedBlockRef : undefined}
                  />
                );
                continue;
              }

              const listType: "ul" | "ol" = inUl ? "ul" : "ol";
              const groupStart = i;
              const items: Array<{ idx: number; id: string }> = [];

              while (i < filteredBlocksWithSuggestions.length) {
                const { block: b } = filteredBlocksWithSuggestions[i];
                const matches = listType === "ul" ? isUnorderedListItemText(b) : isOrderedListItemText(b);
                if (!matches) break;
                items.push({ idx: i, id: b.id });
                i++;
              }
              i--; // adjust because for-loop will i++

              const Wrapper = listType === "ul" ? "ul" : "ol";
              const listClass = listType === "ul" ? "list-disc" : "list-decimal";

              nodes.push(
                <Wrapper key={`list-${filteredBlocksWithSuggestions[groupStart].block.id}`} className={`${listClass} ml-6 my-2`}>
                  {items.map(({ idx, id }) => {
                    const { block: itemBlock, suggestion: itemSuggestion } = filteredBlocksWithSuggestions[idx];
                    const listNumber = listType === "ol" ? extractListNumber(itemBlock.value) : undefined;
                    return (
                      <li key={`li-${id}`} value={listNumber}>
                        <Block
                          key={id}
                          block={itemBlock}
                          index={idx}
                          isSelected={idx === selectedBlockIndex}
                          isExpanded={itemBlock.id === expandedBlockId}
                          focusMode={focusMode}
                          suggestion={itemSuggestion}
                          stripListMarker={true}
                          isListItem={true}
                          ref={idx === selectedBlockIndex ? selectedBlockRef : undefined}
                        />
                      </li>
                    );
                  })}
                </Wrapper>
              );
            }

            return nodes;
          })()}
          <GroupSuggestions />
          <div className="h-[200px]" />
        </div>
      </div>

      <GroupSelector />
    </main>
  );
};
